import Cookies from 'js-cookie';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
}

export const COOKIE_NAMES = {
  PREFERENCES: 'cookie-preferences',
  CONSENT: 'cookie-consent',
} as const;

export const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true, // Always true, cannot be disabled
  analytics: false,
};

// Get cookie preferences
export const getCookiePreferences = (): CookiePreferences => {
  const preferences = Cookies.get(COOKIE_NAMES.PREFERENCES);
  if (!preferences) {
    return DEFAULT_PREFERENCES;
  }
  
  try {
    return { ...DEFAULT_PREFERENCES, ...JSON.parse(preferences) };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

// Set cookie preferences
export const setCookiePreferences = (preferences: CookiePreferences): void => {
  const prefsToStore = { ...preferences, necessary: true }; // Necessary cookies are always enabled
  Cookies.set(COOKIE_NAMES.PREFERENCES, JSON.stringify(prefsToStore), { 
    expires: 365,
    sameSite: 'strict' 
  });
};

// Check if user has given consent
export const hasGivenConsent = (): boolean => {
  return Cookies.get(COOKIE_NAMES.CONSENT) === 'true';
};

// Set consent status
export const setConsentGiven = (): void => {
  Cookies.set(COOKIE_NAMES.CONSENT, 'true', { 
    expires: 365,
    sameSite: 'strict' 
  });
};


// Analytics cookies helpers
export const canUseAnalytics = (): boolean => {
  const preferences = getCookiePreferences();
  return hasGivenConsent() && preferences.analytics;
};

// Clear all cookies except necessary ones
export const clearOptionalCookies = (): void => {
  // Clear Google Analytics cookies
  const gaCookies = [
    '_ga',
    '_ga_',
    '_gid',
    '_gat',
    '_gtag_GA_',
    '__utma',
    '__utmb',
    '__utmc',
    '__utmt',
    '__utmz'
  ];

  gaCookies.forEach(cookieName => {
    // Clear cookie for current domain
    Cookies.remove(cookieName);
    Cookies.remove(cookieName, { path: '/' });
    Cookies.remove(cookieName, { domain: window.location.hostname });
    Cookies.remove(cookieName, { domain: `.${window.location.hostname}` });
    
    // Handle GA cookies with dynamic suffixes
    if (cookieName.endsWith('_')) {
      Object.keys(Cookies.get()).forEach(key => {
        if (key.startsWith(cookieName)) {
          Cookies.remove(key);
          Cookies.remove(key, { path: '/' });
          Cookies.remove(key, { domain: window.location.hostname });
          Cookies.remove(key, { domain: `.${window.location.hostname}` });
        }
      });
    }
  });

  console.log('Cleared Google Analytics cookies');
};